import { cachedGet } from "./apiCache.js";
import { useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import React from "react";

const MODULE_NAME = "_66_options_monthly_pl_chart";

// --- Gear Icon Component ---
const GearIcon = ({ size = 24, color = "#fff" }) => (
    <span
        style={{
            fontSize: size,
            color,
            verticalAlign: "middle",
            cursor: "pointer",
            userSelect: "none",
            lineHeight: 1,
            display: "inline-block",
        }}
        title="Settings"
        aria-label="Settings"
    >
        &#9881;
    </span>
);

// --- Toggle Switch Component ---
function ToggleSwitch({ checked, onChange, label, description }) {
    return (
        <div style={{ marginBottom: 16 }}>
            <label style={{
                display: "inline-flex",
                alignItems: "center",
                cursor: "pointer",
                width: "100%"
            }}>
                <span style={{ marginRight: 12, fontWeight: 600, minWidth: 200 }}>{label}</span>
                <span style={{
                    position: "relative",
                    display: "inline-block",
                    width: 44,
                    height: 24,
                    marginRight: 12
                }}>
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => onChange(e.target.checked)}
                        style={{ opacity: 0, width: 0, height: 0 }}
                    />
                    <span style={{
                        position: "absolute",
                        cursor: "pointer",
                        top: 0, left: 0, right: 0, bottom: 0,
                        background: checked ? "#1976d2" : "#888",
                        borderRadius: 24,
                        transition: "background 0.2s"
                    }} />
                    <span style={{
                        position: "absolute",
                        left: checked ? 22 : 2,
                        top: 2,
                        width: 20,
                        height: 20,
                        background: "#fff",
                        borderRadius: "50%",
                        transition: "left 0.2s"
                    }} />
                </span>
            </label>
            {description && (
                <div style={{ color: "#aaa", fontSize: 11, marginTop: 4, marginLeft: 212 }}>
                    {description}
                </div>
            )}
        </div>
    );
}

function Tooltip({ text }) {
    return (
        <span
            style={{
                marginLeft: 4,
                verticalAlign: "middle",
                cursor: "pointer",
                color: "#888"
            }}
            title={text}
        >
            🛈
        </span>
    );
}

function OptionsMonthlyPLChart({ latest_date, refreshKey }) {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    let dateFromQuery = queryParams.get("date");
    if (!dateFromQuery || !/^\d{4}-\d{2}-\d{2}$/.test(dateFromQuery)) {
        dateFromQuery = null;
    }
    const effectiveDate = dateFromQuery
        || (latest_date && /^\d{4}-\d{2}-\d{2}$/.test(latest_date) ? latest_date : null)
        || (sessionStorage.getItem("latest_date") && /^\d{4}-\d{2}-\d{2}$/.test(sessionStorage.getItem("latest_date")) ? sessionStorage.getItem("latest_date") : null)
        || new Date().toISOString().slice(0, 10);

    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [monthlyData, setMonthlyData] = useState([]);
    const [monthlyDetails, setMonthlyDetails] = useState({});
    const [expandedMonths, setExpandedMonths] = useState({});
    const [showSettingsModal, setShowSettingsModal] = useState(false);
    const [isZoomedOut, setIsZoomedOut] = useState(true);

    useEffect(() => {
        setError("");
        setData(null);
        if (!effectiveDate) {
            setError("No date available for options trade data.");
            return;
        }

        fetch(
            `${process.env.REACT_APP_API_URL}/api/options-trade-table?date=${effectiveDate}&component=_65_options_chart`,
            { credentials: "include" }
        )
            .then(res => res.json())
            .then(data => {
                setData(data);
                aggregateMonthlyPL(data.summary || []);
            })
            .catch(() => setError("Could not fetch options trade data"));
    }, [effectiveDate, refreshKey, location.search]);

    const calculateOptionPL = (openPrice, closePrice, side, qty, multiplier) => {
        const openPx = parseFloat(openPrice || 0);
        const closePx = parseFloat(closePrice || 0);
        const qtyAbs = Math.abs(qty || 0);
        const mult = multiplier || 100;
        const sideName = (side || "").toUpperCase();

        if (sideName === "BUY") {
            return (closePx - openPx) * qtyAbs * mult;
        } else if (sideName === "SELL") {
            return (openPx - closePx) * qtyAbs * mult;
        }
        return 0;
    };

    const aggregateMonthlyPL = (summary) => {
        const monthlyMap = {};
        const detailsMap = {};

        // Filter only closed options
        const closedOptions = summary.filter(option => option.position_status === "Closed");

        if (closedOptions.length === 0) {
            setMonthlyData([]);
            setMonthlyDetails({});
            return;
        }

        // Group by the EXACT same contract key that _54_today_trade_options uses
        const contractMap = {};
        closedOptions.forEach(option => {
            // Use ONLY Symbol, Type (not option_type), Strike, Exp - matching _54 logic
            const contractKey = [option.Symbol, option.Type, option.Strike, option.Exp].join("|");
            if (!contractMap[contractKey]) {
                contractMap[contractKey] = [];
            }
            contractMap[contractKey].push(option);
        });

        // Process each contract's legs
        Object.values(contractMap).forEach(contractLegs => {
            if (contractLegs.length < 2) return; // Need both TO OPEN and TO CLOSE

            // Sort by Pos_Effect to ensure TO OPEN comes first
            contractLegs.sort((a, b) => {
                if (a.Pos_Effect === "TO OPEN" && b.Pos_Effect === "TO CLOSE") return -1;
                if (a.Pos_Effect === "TO CLOSE" && b.Pos_Effect === "TO OPEN") return 1;
                return 0;
            });

            const toOpen = contractLegs.find(leg => leg.Pos_Effect === "TO OPEN");
            const toClose = contractLegs.find(leg => leg.Pos_Effect === "TO CLOSE");

            if (!toOpen || !toClose) return;

            // Use TO CLOSE date for month grouping
            const closeDate = toClose.Exec_Time || toClose.Trade_Date;
            if (!closeDate) return;

            let monthKey;
            let closeDateFormatted;
            if (closeDate.includes("/")) {
                const datePart = closeDate.split(" ")[0];
                const parts = datePart.split("/");
                if (parts.length === 3) {
                    const [mm, dd, yyyy] = parts;
                    const year = yyyy.length === 2 ? `20${yyyy}` : yyyy;
                    monthKey = `${year}-${mm.padStart(2, "0")}`;
                    closeDateFormatted = `${mm}/${dd}/${year.slice(-2)}`;
                } else {
                    return;
                }
            } else {
                return;
            }

            if (!monthlyMap[monthKey]) {
                monthlyMap[monthKey] = {
                    monthKey,
                    year: parseInt(monthKey.split("-")[0]),
                    month: parseInt(monthKey.split("-")[1]),
                    pl: 0,
                    count: 0
                };
                detailsMap[monthKey] = [];
            }

            // Calculate P/L the SAME WAY as _54 does it (use Side + absolute Qty)
            const qty = Math.abs(toOpen.Qty || 0);
            const openPrice = parseFloat(toOpen.Price || 0);
            const closePrice = parseFloat(toClose.Price || 0);
            const multiplier = toOpen.contract_multiplier || 100;
            const side = (toOpen.Side || "").toUpperCase();

            let roundTripPL = 0;
            if (side === "BUY") {
                roundTripPL = (closePrice - openPrice) * qty * multiplier;
            } else if (side === "SELL") {
                roundTripPL = (openPrice - closePrice) * qty * multiplier;
            }

            monthlyMap[monthKey].pl += roundTripPL;
            monthlyMap[monthKey].count += 1;

            detailsMap[monthKey].push({
                symbol: toOpen.Symbol,
                exp: toOpen.Exp,
                openDate: toOpen.Trade_Date,
                closeDate: closeDateFormatted,
                pl: roundTripPL,
                type: toOpen.Type,
                side: toOpen.Side,
                qty: toOpen.Qty,
                openPrice: openPrice,
                closePrice: closePrice
            });
        });

        const sorted = Object.values(monthlyMap).sort((a, b) => {
            if (a.year !== b.year) return a.year - b.year;
            return a.month - b.month;
        });

        setMonthlyData(sorted);
        setMonthlyDetails(detailsMap);
    };

    const getMonthLabel = (month) => {
        const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];
        return monthNames[month - 1];
    };

    const formatValue = (value) => {
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const toggleMonthExpanded = (monthKey) => {
        setExpandedMonths(prev => ({
            ...prev,
            [monthKey]: !prev[monthKey]
        }));
    };

    const renderSettingsModal = () => {
        if (!showSettingsModal) return null;
        return (
            <>
                <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", background: "rgba(24,26,37,0.35)", backdropFilter: "blur(2px)", zIndex: 9998 }} onClick={() => setShowSettingsModal(false)} />
                <div style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", zIndex: 9999, minWidth: 500, minHeight: 300, background: "#181A25", borderRadius: 12, boxShadow: "0 2px 8px rgba(0,255,255,0.08)", padding: 24 }}>
                    <h3 style={{ marginTop: 0, marginBottom: 20, color: "#fff" }}>Chart Settings</h3>
                    <ToggleSwitch checked={isZoomedOut} onChange={setIsZoomedOut} label="Zoom Out Chart" description="Checked = Zoomed out view. Unchecked = Normal view (default)." />
                    <button onClick={() => setShowSettingsModal(false)} style={{ position: "absolute", top: 12, right: 18, background: "none", border: "none", color: "#fff", fontSize: 22, cursor: "pointer" }}>✕</button>
                </div>
            </>
        );
    };

    if (error) return <div style={{ color: "#fff" }}>{error}</div>;
    if (!data) return <div style={{ color: "#fff" }}>Loading...</div>;
    if (data.error && data.error.toLowerCase().includes("no options trades")) {
        return null;
    }
    if (data.error) return <div style={{ color: "#fff" }}>{data.error}</div>;

    if (monthlyData.length === 0) {
        return <div style={{ color: "#aaa", padding: "16px", textAlign: "center" }}>No closed options found for monthly analysis.</div>;
    }

    const maxAbsValue = Math.max(...monthlyData.map(m => Math.abs(m.pl)), 100);
    const barMaxHeight = 200;
    const chartWidth = Math.max(monthlyData.length * 60 + 60, 400);
    const chartHeight = 300;

    /* ═══════════════════════════════════════════════════════════════════════════════════════════════
       🔧 ZOOM SETTINGS - ADJUST HERE
       ═══════════════════════════════════════════════════════════════════════════════════════════════
       Change the zoom scale values below:
       - When isZoomedOut is TRUE:  increases this value → more zoomed in, decreases → more zoomed out
       - When isZoomedOut is FALSE: normal view (1 = 100%)
       
       Current settings:
       - Normal:    1.0   (100% - full size)
       - Zoomed:    0.55  (55% - significantly zoomed out)
       
       Recommended values:
       - 0.5   = 50% zoom (maximum zoom out)
       - 0.6   = 60% zoom (aggressive)
       - 0.7   = 70% zoom (moderate)
       - 0.75  = 75% zoom (light zoom)
       - 1.0   = 100% zoom (normal - no zoom)
       ═══════════════════════════════════════════════════════════════════════════════════════════════ */
    const zoomScale = isZoomedOut ? 0.55 : 1;

    /* ═══════════════════════════════════════════════════════════════════════════════════════════════
       🎯 CANVAS SIZE ADJUSTMENT - SCALES WITH ZOOM
       ═══════════════════════════════════════════════════════════════════════════════════════════════
       This container maintains proper dimensions when zoom changes:
       - Height: chartHeight / zoomScale gives us full space even when scaled
       - When zoomed out (0.55): container becomes taller to show full chart
       - When normal (1.0): displays normal chartHeight
       
       Formula: Since we're scaling DOWN with transform, we need to give MORE space
       to the container so scaled content fits properly
       ═══════════════════════════════════════════════════════════════════════════════════════════════ */
    const containerHeight = chartHeight / zoomScale;

    return (
        <div data-analytics={MODULE_NAME} style={{ 
            marginTop: "24px", 
            padding: "16px", 
            background: "#0f0f0f", 
            borderRadius: 6,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box"
        }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexShrink: 0 }}>
                <h4 style={{ margin: 0, color: "#fff" }}>
                    📊 Options P/L by Month (Close Date)
                    <Tooltip text="Closed options grouped by close date month" />
                </h4>
                <span onClick={() => setShowSettingsModal(true)} style={{ cursor: "pointer", marginLeft: "auto" }}><GearIcon size={24} color="#fff" /></span>
            </div>

            <div style={{
                transform: `scale(${zoomScale})`,
                transformOrigin: "top left",
                transition: "transform 0.3s ease",
                height: containerHeight,
                overflow: "visible",
                marginBottom: Math.ceil(containerHeight * 0.15),
                flexShrink: 0
            }}>
                <svg
                    style={{ width: "100%", minHeight: chartHeight, background: "#111", borderRadius: 4, border: "1px solid #333" }}
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    preserveAspectRatio="xMinYMid meet"
                >
                    {/* Y-axis */}
                    <line x1="50" y1="20" x2="50" y2={chartHeight - 40} stroke="#444" strokeWidth="1" />

                    {/* X-axis */}
                    <line x1="50" y1={chartHeight - 40} x2={chartWidth - 20} y2={chartHeight - 40} stroke="#444" strokeWidth="1" />

                    {/* Y-axis grid lines and labels */}
                    {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
                        const yPos = (chartHeight - 40) - ratio * barMaxHeight;
                        const value = maxAbsValue * ratio;
                        return (
                            <g key={`y-grid-${idx}`}>
                                <line
                                    x1="50"
                                    y1={yPos}
                                    x2={chartWidth - 20}
                                    y2={yPos}
                                    stroke="#222"
                                    strokeWidth="1"
                                    strokeDasharray="4,4"
                                />
                                <text
                                    x="45"
                                    y={yPos + 4}
                                    textAnchor="end"
                                    fill="#888"
                                    fontSize="10"
                                >
                                    ${formatValue(value)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Bars and month labels */}
                    {monthlyData.map((month, idx) => {
                        const xPos = 60 + idx * 60;
                        const isPositive = month.pl >= 0;
                        const barColor = isPositive ? "#4CAF50" : "#F44336";
                        const barHeight = (Math.abs(month.pl) / maxAbsValue) * barMaxHeight;
                        const barTopY = isPositive
                            ? (chartHeight - 40) - barHeight
                            : (chartHeight - 40);

                        return (
                            <g key={month.monthKey}>
                                {/* Vertical bar */}
                                <rect
                                    x={xPos - 15}
                                    y={barTopY}
                                    width="30"
                                    height={barHeight}
                                    fill={barColor}
                                    opacity="0.85"
                                    style={{
                                        transition: "opacity 0.2s",
                                        cursor: "pointer"
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.opacity = "1";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.opacity = "0.85";
                                    }}
                                >
                                    <title>{`${getMonthLabel(month.month)} ${month.year}: $${formatValue(month.pl)} (${month.count} trades)`}</title>
                                </rect>

                                {/* Value label above/below bar */}
                                <text
                                    x={xPos}
                                    y={isPositive ? barTopY - 5 : barTopY + barHeight + 15}
                                    textAnchor="middle"
                                    fill={barColor}
                                    fontSize="11"
                                    fontWeight="600"
                                >
                                    ${formatValue(month.pl)}
                                </text>

                                {/* Month label at bottom */}
                                <text
                                    x={xPos}
                                    y={chartHeight - 20}
                                    textAnchor="middle"
                                    fill="#aaa"
                                    fontSize="12"
                                    fontWeight="500"
                                >
                                    {getMonthLabel(month.month)} '{String(month.year).slice(-2)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Legend */}
                    <g>
                        <rect x={chartWidth - 150} y="10" width="12" height="12" fill="#4CAF50" />
                        <text x={chartWidth - 135} y="19" fill="#aaa" fontSize="11">Profit</text>

                        <rect x={chartWidth - 70} y="10" width="12" height="12" fill="#F44336" />
                        <text x={chartWidth - 55} y="19" fill="#aaa" fontSize="11">Loss</text>
                    </g>
                </svg>
            </div>

            {/* Summary statistics */}
            <div style={{ marginTop: "16px", display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px", marginBottom: "24px", flexShrink: 0, position: "relative", zIndex: 2 }}>
                <div style={{ padding: "12px", background: "#1a1a1a", borderRadius: 4, borderLeft: "3px solid #4CAF50" }}>
                    <div style={{ color: "#aaa", fontSize: "11px", marginBottom: "4px" }}>Winning Months</div>
                    <div style={{ color: "#4CAF50", fontSize: "16px", fontWeight: "bold" }}>
                        {monthlyData.filter(m => m.pl > 0).length}
                    </div>
                </div>
                <div style={{ padding: "12px", background: "#1a1a1a", borderRadius: 4, borderLeft: "3px solid #F44336" }}>
                    <div style={{ color: "#aaa", fontSize: "11px", marginBottom: "4px" }}>Losing Months</div>
                    <div style={{ color: "#F44336", fontSize: "16px", fontWeight: "bold" }}>
                        {monthlyData.filter(m => m.pl < 0).length}
                    </div>
                </div>
                <div style={{ padding: "12px", background: "#1a1a1a", borderRadius: 4, borderLeft: "3px solid #2196F3" }}>
                    <div style={{ color: "#aaa", fontSize: "11px", marginBottom: "4px" }}>Total Closed Trades</div>
                    <div style={{ color: "#2196F3", fontSize: "16px", fontWeight: "bold" }}>
                        {monthlyData.reduce((sum, m) => sum + m.count, 0)}
                    </div>
                </div>
            </div>

            {/* Details Table */}
            <div style={{ marginTop: "24px", flexGrow: 1, display: "flex", flexDirection: "column", position: "relative", zIndex: 2, clear: "both" }}>
                <h4 style={{ color: "#fff", marginBottom: "12px", fontSize: "14px", flexShrink: 0 }}>📋 Trade Details by Month</h4>
                <div style={{ overflowX: "auto", borderRadius: 4, border: "1px solid #333", flexGrow: 1 }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", color: "#fff" }}>
                        <thead>
                            <tr style={{ backgroundColor: "#1a1a1a", borderBottom: "1px solid #444" }}>
                                <th style={{ padding: "12px", textAlign: "left", fontWeight: 600, color: "#aaa", minWidth: "100px" }}>Month</th>
                                <th style={{ padding: "12px", textAlign: "left", fontWeight: 600, color: "#aaa", minWidth: "80px" }}>Symbol</th>
                                <th style={{ padding: "12px", textAlign: "left", fontWeight: 600, color: "#aaa", minWidth: "100px" }}>Exp</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa", minWidth: "100px" }}>Open Date</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa", minWidth: "100px" }}>Close Date</th>
                                <th style={{ padding: "12px", textAlign: "right", fontWeight: 600, color: "#aaa", minWidth: "80px" }}>P/L</th>
                                <th style={{ padding: "12px", textAlign: "center", fontWeight: 600, color: "#aaa", minWidth: "60px" }} />
                            </tr>
                        </thead>
                        <tbody>
                            {monthlyData.map((month, monthIdx) => {
                                const monthKey = month.monthKey;
                                const isExpanded = expandedMonths[monthKey];
                                const trades = monthlyDetails[monthKey] || [];

                                return (
                                    <React.Fragment key={monthKey}>
                                        {/* Month Header Row */}
                                        <tr
                                            style={{
                                                backgroundColor: monthIdx % 2 === 0 ? "#111" : "#0d0d0d",
                                                borderBottom: "1px solid #333",
                                                fontWeight: "bold",
                                                cursor: "pointer"
                                            }}
                                            onClick={() => toggleMonthExpanded(monthKey)}
                                        >
                                            <td style={{ padding: "12px", textAlign: "left", display: "flex", alignItems: "center", gap: "8px" }}>
                                                <span style={{ fontSize: "12px", color: "#aaa", width: "16px" }}>
                                                    {isExpanded ? "▼" : "▶"}
                                                </span>
                                                {getMonthLabel(month.month)} {month.year}
                                            </td>
                                            <td colSpan="4" style={{ padding: "12px", color: "#aaa", fontSize: "12px" }}>
                                                {trades.length} trade{trades.length !== 1 ? "s" : ""}
                                            </td>
                                            <td style={{ padding: "12px", textAlign: "right", fontWeight: 600 }}>
                                                <span style={{ color: month.pl >= 0 ? "#4CAF50" : "#F44336" }}>
                                                    ${formatValue(month.pl)}
                                                </span>
                                            </td>
                                            <td />
                                        </tr>

                                        {/* Expanded Trade Rows */}
                                        {isExpanded && trades.map((trade, tradeIdx) => {
                                            const isProfit = trade.pl >= 0;
                                            return (
                                                <tr
                                                    key={`${monthKey}-${tradeIdx}`}
                                                    style={{
                                                        backgroundColor: "#1a1d26",
                                                        borderBottom: "1px solid #222",
                                                        fontSize: "12px",
                                                        borderLeft: "3px solid #1976d2"
                                                    }}
                                                >
                                                    <td style={{ padding: "8px 12px", paddingLeft: "40px", color: "#bbb" }}>—</td>
                                                    <td style={{ padding: "8px 12px", color: "#fff", fontWeight: 500 }}>{trade.symbol}</td>
                                                    <td style={{ padding: "8px 12px", color: "#bbb" }}>{trade.exp}</td>
                                                    <td style={{ padding: "8px 12px", textAlign: "center", color: "#bbb" }}>{trade.openDate}</td>
                                                    <td style={{ padding: "8px 12px", textAlign: "center", color: "#bbb" }}>{trade.closeDate}</td>
                                                    <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>
                                                        <span style={{ color: isProfit ? "#4CAF50" : "#F44336" }}>
                                                            ${formatValue(trade.pl)}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: "8px 11px", textAlign: "center", color: "#888", fontSize: "10px" }}>
                                                        {trade.type} {trade.side}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {renderSettingsModal()}
        </div>
    );
}

export default OptionsMonthlyPLChart;